import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { Spinner } from "stories/components";
import SummaryMetricCard from "components/SummaryMetricCard/SummaryMetricCard";
import { thousandFormat } from "services/helpers";
import {
  aggregatePortfoliosSummary,
  isPortfoliosSummaryEmpty,
} from "utils/portfolioSummaryAggregate";

import "./PortfoliosSummaryHero.css";

function fmtEuro(value, currencyLabel = "€", { withPlus = false } = {}) {
  const x = Number(value);
  if (!Number.isFinite(x)) return "—";
  const neg = x < 0;
  const abs = thousandFormat(Math.abs(x).toFixed(2));
  const prefix = neg ? "-" : withPlus && x > 0 ? "+" : "";
  return `${prefix}${abs} ${currencyLabel}`.trim();
}

/**
 * Etusivun salkkujen yhteenveto — sama data kuin portfolio_profit_summary.
 */
const PortfoliosSummaryHero = ({
  portfoliosSummary,
  loading,
  accountUserType,
  selectedPortfolioName,
  portfolioCount,
  currencyLabel,
  className,
}) => {
  const rootClass = ["portfolios-summary-hero", className].filter(Boolean).join(" ");
  const { t } = useTranslation();

  const portfolioKeys = useMemo(() => {
    if (!portfoliosSummary || typeof portfoliosSummary !== "object" || Array.isArray(portfoliosSummary)) {
      return [];
    }
    return Object.keys(portfoliosSummary);
  }, [portfoliosSummary]);

  const aggregated = useMemo(
    () => aggregatePortfoliosSummary(portfoliosSummary),
    [portfoliosSummary]
  );

  const cur = currencyLabel || "€";

  const scopeLabel =
    accountUserType === "admin" && portfolioKeys.length > 1
      ? t("portfolios_summary_hero_scope_all", "Kaikki salkut")
      : selectedPortfolioName ||
        (portfolioKeys.length === 1
          ? t("portfolios_summary_hero_scope_single", "Oma salkku")
          : t("portfolios_summary_hero_scope_all", "Kaikki salkut"));

  if (loading && isPortfoliosSummaryEmpty(portfoliosSummary)) {
    return (
      <section className={rootClass}>
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      </section>
    );
  }

  if (!aggregated) {
    return null;
  }

  const hankinta = Number(aggregated.Hankintahinta) || 0;
  const markkina = Number(aggregated.Markkina_arvo) || 0;
  const unrealizedRaw = aggregated.Realisoitumattomat_tuotot;
  const unrealized = Number.isFinite(Number(unrealizedRaw))
    ? Number(unrealizedRaw)
    : markkina - hankinta;
  const realized = Number(aggregated.Realisoituneet_tuotot) || 0;

  const pct =
    hankinta > 0 ? ((markkina - hankinta) / hankinta) * 100 : 0;
  const pctStr = `${pct >= 0 ? "+" : ""}${pct.toFixed(1).replace(".", ",")}%`;

  const unrealizedTone = unrealized >= 0 ? "positive" : "negative";
  const realizedTone = realized >= 0 ? "positive" : "negative";

  return (
    <section className={rootClass}>
      <h2 className="portfolios-summary-hero__title">
        {t("portfolios_summary_hero_title", "Tervetuloa")}
      </h2>

      <div className="portfolios-summary-hero__grid">
        <SummaryMetricCard
          label={t("portfolios_summary_hero_acquisition", "Hankintameno")}
          value={fmtEuro(hankinta, cur)}
          footer={
            accountUserType === "admin" && portfolioCount > 1
              ? t(
                  "portfolios_summary_hero_portfolio_count_footer",
                  "Salkkuja yhteensä {{count}}kpl",
                  { count: portfolioCount }
                )
              : scopeLabel
          }
        />
        <SummaryMetricCard
          label={t("portfolios_summary_hero_market_value", "Markkina-arvo")}
          value={fmtEuro(markkina, cur)}
          footer={t("portfolios_summary_hero_market_hint", "Reaaliaikainen")}
        />
        <SummaryMetricCard
          label={t("portfolios_summary_hero_total_return", "Kokonaistuotto")}
          value={fmtEuro(unrealized, cur, { withPlus: true })}
          footer={
            <>
              <i className={`fas fa-arrow-${unrealized >= 0 ? "up" : "down"} mr-1`} aria-hidden />
              {pctStr}
            </>
          }
          valueTone={unrealizedTone}
          footerTone={unrealizedTone}
        />
        <SummaryMetricCard
          label={t("portfolios_summary_hero_realized", "Realisoidut tuotot")}
          value={fmtEuro(realized, cur, { withPlus: true })}
          footer={
            <>
              <i className={`fas fa-arrow-${realized >= 0 ? "up" : "down"} mr-1`} aria-hidden />
              {t("portfolios_summary_hero_realized_hint", "vuoden alusta")}
            </>
          }
          valueTone={realizedTone}
          footerTone={realizedTone}
        />
      </div>
    </section>
  );
};

PortfoliosSummaryHero.propTypes = {
  portfoliosSummary: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  loading: PropTypes.bool,
  accountUserType: PropTypes.string,
  selectedPortfolioName: PropTypes.string,
  portfolioCount: PropTypes.number,
  currencyLabel: PropTypes.string,
  className: PropTypes.string,
};

export default PortfoliosSummaryHero;
